import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Megaphone, Save, Image as ImageIcon, Link2, Upload, MonitorPlay } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

function compressImage(file, max = 1080) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AdminBanner() {
  const [enabled, setEnabled] = useState(false);
  const [messages, setMessages] = useState([""]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/banner")
      .then((r) => {
        setEnabled(!!r.data.enabled);
        setMessages(r.data.messages?.length ? r.data.messages : [""]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setMsg = (i, v) => { const m = [...messages]; m[i] = v; setMessages(m); };
  const addMsg = () => setMessages([...messages, ""]);
  const removeMsg = (i) => { const m = messages.filter((_, x) => x !== i); setMessages(m.length ? m : [""]); };

  const save = async () => {
    setSaving(true);
    try {
      const clean = messages.map((m) => m.trim()).filter(Boolean);
      const r = await api.put("/admin/banner", { enabled, messages: clean });
      setMessages(r.data.messages?.length ? r.data.messages : [""]);
      toast.success("Bandeau enregistré !");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#00E676]" /></div>;

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-banner">
      <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2"><Megaphone size={18} className="text-[#00E676]" /> Bandeau défilant</h2>
            <p className="text-sm text-[#697C6F] mt-1">Affiché sous le menu sur toutes les pages membres.</p>
          </div>
          <button
            onClick={() => setEnabled((v) => !v)}
            data-testid="banner-toggle"
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${enabled ? "bg-[#00E676]" : "bg-[#2A3B30]"}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
        <div className={`mt-2 text-xs font-bold ${enabled ? "text-[#39FF14]" : "text-[#697C6F]"}`}>
          {enabled ? "● Bandeau ACTIVÉ" : "○ Bandeau MASQUÉ"}
        </div>
      </div>

      <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-5 space-y-3">
        <div className="text-xs uppercase tracking-wide text-[#697C6F]">Messages (défilent à la suite)</div>
        {messages.map((m, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={m}
              onChange={(e) => setMsg(i, e.target.value)}
              placeholder={`Message ${i + 1} — ex : Quinté du jour à Vincennes, ne le ratez pas !`}
              data-testid={`banner-msg-${i}`}
              className="flex-1 bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-[#697C6F] focus:outline-none focus:ring-2 focus:ring-[#39FF14]"
            />
            <button onClick={() => removeMsg(i)} data-testid={`banner-remove-${i}`} className="p-2 rounded-md text-[#FF3B30] hover:bg-[#FF3B30]/10 shrink-0">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button onClick={addMsg} data-testid="banner-add" className="text-sm text-[#39FF14] flex items-center gap-1">
          <Plus size={14} /> Ajouter un message
        </button>
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="banner-save"
        className="w-full py-3 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#00E676] to-[#39FF14] disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={18} /> Enregistrer le bandeau</>}
      </button>

      <AdminAnnouncement />
    </div>
  );
}

function AdminAnnouncement() {
  const [enabled, setEnabled] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [image, setImage] = useState("");
  const [imgMode, setImgMode] = useState("upload"); // "upload" | "url"
  const [frequency, setFrequency] = useState("always"); // "always" | "once"
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/admin/announcement")
      .then((r) => {
        setEnabled(!!r.data.enabled);
        setTitle(r.data.title || "");
        setText(r.data.text || "");
        setImage(r.data.image || "");
        setFrequency(r.data.frequency || "always");
        if ((r.data.image || "").startsWith("http")) setImgMode("url");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(file);
      setImage(dataUrl);
    } catch {
      toast.error("Échec du chargement de l'image");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!text.trim() && !title.trim() && !image.trim()) {
      toast.error("Ajoutez au moins un titre, un texte ou une image.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/announcement", { enabled, title: title.trim(), text: text.trim(), image: image.trim(), frequency });
      toast.success("Message global enregistré !");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const tab = (v, label, Icon) => (
    <button
      onClick={() => setImgMode(v)}
      data-testid={`ann-imgmode-${v}`}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${imgMode === v ? "bg-[#00E676] text-[#04180a]" : "bg-[#0F1812] text-[#A1B5A8] hover:text-white"}`}
    >
      <Icon size={13} /> {label}
    </button>
  );

  return (
    <div className="mt-10 pt-8 border-t border-[#00E676]/10 space-y-6" data-testid="admin-announcement">
      <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2"><MonitorPlay size={18} className="text-[#00E676]" /> Message global à la connexion</h2>
            <p className="text-sm text-[#697C6F] mt-1">Popup affichée à tous les joueurs quand ils se connectent.</p>
          </div>
          <button
            onClick={() => setEnabled((v) => !v)}
            data-testid="ann-toggle"
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${enabled ? "bg-[#00E676]" : "bg-[#2A3B30]"}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
        <div className={`mt-2 text-xs font-bold ${enabled ? "text-[#39FF14]" : "text-[#697C6F]"}`}>
          {enabled ? "● Message ACTIVÉ" : "○ Message MASQUÉ"}
        </div>
      </div>

      <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-5 space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-wide text-[#697C6F]">Titre (optionnel)</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex : Bienvenue sur A Nous Turf !"
            data-testid="ann-title"
            className="w-full bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-[#697C6F] focus:outline-none focus:ring-2 focus:ring-[#39FF14]"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-wide text-[#697C6F]">Texte</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            placeholder="Votre message pour les joueurs..."
            data-testid="ann-text"
            className="w-full bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-[#697C6F] focus:outline-none focus:ring-2 focus:ring-[#39FF14] resize-y"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <label className="text-xs uppercase tracking-wide text-[#697C6F] flex items-center gap-1.5"><ImageIcon size={13} /> Image (optionnelle)</label>
            <div className="flex items-center gap-1.5">
              {tab("upload", "Fichier", Upload)}
              {tab("url", "URL", Link2)}
            </div>
          </div>

          {imgMode === "upload" ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="ann-upload-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-[#0F1812] border border-[#00E676]/20 text-white hover:border-[#39FF14]"
              >
                {uploading ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} Choisir une image
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="ann-file-input" />
            </div>
          ) : (
            <input
              value={image.startsWith("data:") ? "" : image}
              onChange={(e) => setImage(e.target.value)}
              placeholder="https://exemple.com/mon-image.jpg"
              data-testid="ann-image-url"
              className="w-full bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-[#697C6F] focus:outline-none focus:ring-2 focus:ring-[#39FF14]"
            />
          )}

          {image ? (
            <div className="relative inline-block mt-1">
              <img src={image} alt="" className="max-h-40 rounded-lg border border-[#00E676]/20" data-testid="ann-image-preview" />
              <button
                onClick={() => setImage("")}
                data-testid="ann-image-remove"
                className="absolute -top-2 -right-2 grid h-7 w-7 place-items-center rounded-full bg-[#FF3B30] text-white"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-wide text-[#697C6F]">Fréquence d'affichage</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setFrequency("always")}
              data-testid="ann-freq-always"
              className={`px-3 py-2.5 rounded-lg text-sm font-semibold text-left transition-colors ${frequency === "always" ? "bg-[#00E676] text-[#04180a]" : "bg-[#0F1812] text-[#A1B5A8] border border-[#00E676]/15"}`}
            >
              À chaque connexion
            </button>
            <button
              onClick={() => setFrequency("once")}
              data-testid="ann-freq-once"
              className={`px-3 py-2.5 rounded-lg text-sm font-semibold text-left transition-colors ${frequency === "once" ? "bg-[#00E676] text-[#04180a]" : "bg-[#0F1812] text-[#A1B5A8] border border-[#00E676]/15"}`}
            >
              Une seule fois par joueur
            </button>
          </div>
        </div>
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="ann-save"
        className="w-full py-3 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#FFD700] to-[#FFC400] disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={18} /> Enregistrer le message global</>}
      </button>
    </div>
  );
}
